<template>
	<AccessibleSection
		name="chat-header"
		:tabOrder="chatHeaderTabOrder"
		class="header shadow-left"
		:class="{ embedded: store.config.embedded }"
	>
		<div
			class="message-control"
			v-bind:class="{
				'embedded-message-control': store.config.embedded,
			}"
			tabindex="-1"
		>
			<a
				v-if="store.config.embedded"
				:href="currentUrl"
				target="_blank"
				class="embedded-logo-link"
			>
				<div class="embedded-logo">
					<img src="/images/pronto/embedded-logo.svg" />
				</div>

				<div
					v-if="inAppUnreads > 0"
					class="embedded-alert-badge"
					v-text="inAppUnreads"
				/>
			</a>

			<div v-if="condensedSize" class="drawer-toggle-wrapper">
				<button
					class="drawer-toggle-btn"
					@click="toggleDrawer"
					:aria-label="$t('accessibility_hamburger_menu')"
				>
					<ChevronIcon left type="solid" />
				</button>
				<UnreadBadge
					v-if="condensedUnreads > 0"
					class="drawer-toggle-badge"
					:unread="condensedUnreads"
					@click="toggleDrawer"
				/>
			</div>

			<div class="group-title noSelect">
				<GroupIcon
					class="group-icon"
					v-if="!chat.isdm"
					:chatId="chat.id"
					:key="chat.id"
				/>
				<ProfilePizza
					class="group-profile"
					v-if="chat.isMultiDM"
					:userIds="dmMemberIds"
					:size="32"
					showTotal
					alphabetical
				/>
				<ProfilePic
					class="group-profile"
					v-if="chat.isdm && partner"
					:user="partner"
					enableOnline
				/>

				<div class="group-title-text">
					<div
						class="group-title-multi-dm"
						v-if="chat.isMultiDM"
						v-text="$t('chat_directmessage_start_title')"
					/>
					<div
						class="group-title-category"
						v-if="!chat.isdm"
						v-text="
							category.displayname ||
							$t('chat_drawer_section_bubbles_uncatagorized')
						"
					/>
					<div class="group-title-name" :class="{ 'dm-title': chat.isdm }">
						{{ chat.displayname }}
						<EyeSlashIcon
							type="solid"
							v-if="chat.ishidden"
							class="hidden-group-icon"
						/>
					</div>
					<div
						class="partner-activity"
						v-if="showDMActivity"
						v-text="dmActivity"
					/>
				</div>
			</div>
			<div class="group-actions">
				<UnreadBadge
					v-if="unclaimedTaskCount > 0 && !store.config.wrapper"
					class="unread-tasks"
					:unread="unclaimedTaskCount"
					@click="showUnclaimed = true"
				/>

				<VideoIcon
					v-if="meetingsEnabled"
					class="meetings-icon pointer"
					:class="{ disabled: !canStartMeeting }"
					@click="meetingsPreCheck"
					:aria-label="$t('accessibility_start_meeting')"
				/>

				<ReelIcon
					v-if="showThreadsButton"
					class="threads-icon pointer"
					type="solid"
					halo
					@click="openThreadBrowser"
					:aria-label="$t('chat_threadbrowser_title')"
				/>

				<SettingsMenu v-if="!condensedSize" :chatId="chat.id" />

				<AppLink
					v-if="condensedSize"
					class="group-settings-link"
					:to="{ name: 'groupoptions', params: { id: chat.id } }"
				>
					<GearIcon class="group-settings-gear" type="regular" />
				</AppLink>
			</div>
		</div>
	</AccessibleSection>

	<UnclaimedTasks v-if="showUnclaimed" @close="showUnclaimed = false" />

	<NewMeetingPopup v-if="showMeetingPopup" @close="showMeetingPopup = false" />

	<ThreadBrowser
		:chatId="chatId"
		:isOpen="showThreadBrowser"
		@close="showThreadBrowser = false"
		@open-thread="onOpenThread"
		@create-thread="onCreateThread"
	/>

	<StandardModal
		v-if="showMeetingsDisabledModal"
		open
		small
		:title="$t('common_no_permission')"
		:confirmText="$t('common_button_done')"
		:confirmProps="{ danger: true }"
		hideCancel
		@confirm="showMeetingsDisabledModal = false"
		@cancel="showMeetingsDisabledModal = false"
		class="meetings-error-modal"
	>
		<CircleExclamationIcon type="regular" halo />
		<div class="meetings-error-message">
			{{ $t('meetings_no_permission') }}
		</div>
	</StandardModal>
</template>

<script setup>
import { ref, watch, computed, onMounted } from 'vue'
import store, {
	getChat,
	getCategoryForChat,
	getUser,
	getStat,
	getMyUser,
	getUsersForBubble,
} from '@/store'
import {
	AppLink,
	GroupIcon,
	UnreadBadge,
	StandardModal,
	ProfilePizza,
	ProfilePic,
} from '@/components/common'
import SettingsMenu from '@/components/Home/SettingsMenu'
import { AccessibleSection } from '@/components/common/Accessible'
import NewMeetingPopup from '@/components/Meeting/NewMeetingPopup'
import { useEventBus } from '@/helpers/bus'
import {
	CloseIcon,
	ArrowUpRightFromSquareIcon,
	VideoIcon,
	EyeSlashIcon,
	CircleExclamationIcon,
	ReelIcon,
	ChevronIcon,
	GearIcon,
} from '@/icons'
import { calculateLastActiveText, openPopout } from '@/helpers/utils'
import { useI18n } from 'vue-i18n'
import { orgFlag, MEETINGS } from '@/helpers/flags'
import UnclaimedTasks from '@/components/UnclaimedTasks'
import ThreadBrowser from '@/components/Chat/ThreadBrowser'
import {
	chatCan,
	CREATE_MEETING,
	CREATE_VIDEOSESSION,
} from '@/helpers/permissions'

const { t } = useI18n()

defineOptions({ name: 'MessageListHeader' })
const props = defineProps({
	chatId: Number,
	inAppUnreads: Number,
})

const meetingsEnabled = orgFlag(MEETINGS)
const chat = ref(getChatFromStore())
const category = ref(getCategoryFromStore())
const showMeetingsDisabledModal = ref(false)
const showMeetingPopup = ref(false)
const showUnclaimed = ref(false)
const showThreadBrowser = ref(false)
const dmActivity = computed(() => calculateDMActivity())
const showDMActivity = computed(() => !!dmActivity.value)
const canStartMeeting = computed(
	() =>
		chatCan(chat.value, CREATE_MEETING) ||
		chatCan(chat.value, CREATE_VIDEOSESSION)
)
const showThreadsButton = computed(() => !chat.value.is_thread)
const currentUrl = computed(() => window.location.href.split('?')[0])
const unclaimedTaskCount = computed(
	() => getStat(props.chatId)?.unclaimed_task_count || 0
)
const dmMemberIds = computed(() =>
	chat.value.isMultiDM ? getUsersForBubble(chat.value.id).map((u) => u.id) : []
)
const isSelfDM = computed(
	() => chat.value.isdm && chat.value.dm_memberships_count === 1
)
const partner = computed(() =>
	isSelfDM.value ? getMyUser() : getUser(chat.value.dmpartner_id)
)
const EventBus = useEventBus()
useEventBus('chat-update', getDataFromStore)
useEventBus('condensedSize', (value) => {
	condensedSize.value = value
})
watch(() => props.chatId, getDataFromStore)

const condensedSize = ref(window.innerWidth <= 800)

const chatHeaderTabOrder = computed(() => {
	if (condensedSize.value) {
		return [
			{ css: '.drawer-toggle-btn', clickOnEnter: true },
			{ css: '.unread-tasks', clickOnEnter: true },
			{ css: '.meetings-icon', clickOnEnter: true },
			{ css: '.threads-icon', clickOnEnter: true },
			{ css: '.group-settings-gear', clickOnEnter: true },
		]
	}
	return [
		{ css: '.unread-tasks', clickOnEnter: true },
		{ css: '.meetings-icon', clickOnEnter: true },
		{ css: '.threads-icon', clickOnEnter: true },
		{ css: '.group-settings-menu', clickOnEnter: true, force: true },
	]
})

function toggleDrawer() {
	EventBus.$emit('toggleDrawer')
}

const condensedUnreads = computed(() => props.inAppUnreads || 0)

function getChatFromStore() {
	return getChat(props.chatId) || {}
}

function getCategoryFromStore() {
	return getCategoryForChat(props.chatId) || {}
}

function getDataFromStore() {
	chat.value = getChatFromStore()
	category.value = getCategoryFromStore()
}

function calculateDMActivity() {
	if (!chat.value || !chat.value.dmpartner_id) {
		// if no chat data, show nothing
		return null
	}
	const partner = getUser(chat.value.dmpartner_id)
	return calculateLastActiveText(partner)
}

function meetingsPreCheck() {
	if (!meetingsEnabled || !chat.value) return

	if (chat.value.meeting_id || chat.value.videosession_id) {
		return openPopout(`/meeting/${chat.value.id}`)
	}
	if (!canStartMeeting.value) {
		showMeetingsDisabledModal.value = true
		return
	}

	showMeetingPopup.value = true
}

function closeWrapper() {
	if (!store.config.wrapper) return
	postFromIframe({ type: 'closeWrapper' })
}

function openThreadBrowser() {
	showThreadBrowser.value = !showThreadBrowser.value
}

function onOpenThread(thread) {
	showThreadBrowser.value = false
	EventBus.$emit('open-thread', thread)
}

function onCreateThread(data) {
	showThreadBrowser.value = false
	EventBus.$emit('open-thread', {
		id: null,
		parentChatId: data?.parentChatId || props.chatId,
	})
}

onMounted(() => {
	if (window.location.search.includes('startMeeting')) {
		showMeetingPopup.value = true
	}
})
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.message-control {
	height: 60px;
	border-bottom: solid 1px var(--neutral-40040);
	background-color: var(--neutral-0);
	position: relative;
	outline: 0;
	@include flex-spread;

	&.embedded-message-control {
		border-top-left-radius: 0px;
	}

	.meetings-icon {
		font-size: 28px;
		color: var(--teal-300);
		&.disabled {
			color: var(--neutral-500);
		}
	}

	.threads-icon {
		font-size: 22px;
		transform: rotate(30deg);
		color: var(--neutral-400);
		&::before {
			opacity: 0;
		}
		&:hover {
			&::before {
				opacity: 0.1;
			}
		}
	}

	.group-title {
		@include flex(12px);
		padding-left: 16px;
		.group-icon,
		.group-profile {
			flex-shrink: 0;
		}
		.group-icon {
			background-color: var(--neutral-100);
		}
		.group-title-text {
			@include flex-column;
			.group-title-category,
			.group-title-multi-dm {
				text-transform: uppercase;
				font-size: 10px;
				line-height: 12px;
				@include fontFamily(400);
				@include clamp(1);
			}
			.group-title-name {
				font-size: 18px;
				@include fontFamily(800);
				@include clamp(1);
				.hidden-group-icon {
					font-size: 16px;
					margin-left: 12px;
					color: var(--neutral-500);
				}
			}
			.partner-activity {
				font-size: 11px;
				color: var(--neutral-500);
			}
		}
	}

	.group-actions {
		@include flex(24px);
		padding-right: 24px;
		margin-left: 8px;
		.group-settings-link {
			display: flex;
			align-items: center;
		}
		.group-settings-gear {
			color: var(--neutral-400);
			font-size: 20px;
			cursor: pointer;
		}
	}

	.unread-tasks {
		cursor: pointer;
		$size: 36px;
		@include flex;
		justify-content: center;
		width: $size !important;
		height: $size !important;
		background: var(--teal-100) !important;
		color: var(--teal-300) !important;
		border-radius: calc(#{$size} / 2) !important;
	}

	.group-icon {
		font-size: 32px;
	}

	.hidden-group-icon {
		width: 16px;
		height: 14px;
		margin-left: 6px;
		vertical-align: middle;
	}

	.embedded-logo-link {
		.embedded-logo {
			position: absolute;
			left: 38px;
			bottom: 7px;
			img {
				height: 36px;
			}
		}
		.embedded-alert-badge {
			width: 22px;
			height: 22px;
			border-radius: 50%;
			padding-top: 3.2px;
			background-color: var(--red-300);
			font-family: Avenir-Medium;
			font-size: 12px;
			text-align: center;
			color: white;
			position: absolute;
			left: 56px;
			bottom: 33px;
		}
	}
}

@media screen and (max-width: 800px) {
	.message-control {
		.drawer-toggle-wrapper {
			position: absolute;
			left: 8px;
			top: 50%;
			transform: translateY(-50%);
			display: flex;
			align-items: center;
			z-index: 2;
		}
		.drawer-toggle-btn {
			padding: 0;
			background: none;
			border: none;
			cursor: pointer;

			.pronto-icon {
				font-size: 20px;
				color: var(--neutral-400);
				&:hover {
					color: var(--neutral-300);
				}
				&:active {
					color: var(--neutral-500);
				}
			}
		}
		.drawer-toggle-badge {
			cursor: pointer;
		}
		.group-title {
			margin-left: 58px !important;
			padding-left: 0;
		}
		.group-actions {
			@include flex(16px);
			padding-right: 16px;
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.message-control {
		background-color: var(--neutral-100);
		border-left: 1px solid var(--neutral-40040);
		.online-indicator {
			border-color: var(--neutral-0) !important;
		}
		.unread-tasks {
			background: var(--teal-200) !important;
		}
		.pronto-group-icon {
			background-color: var(--neutral-0) !important;
		}
	}
}

@include overrideContrast {
	.hamburger-inner {
		background-color: var(--neutral-800) !important;
	}
	.message-control {
		.unread-tasks {
			color: white !important;
		}
	}
}
</style>
